import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Address, PublicClient, Hex } from 'viem'
import { hexToString } from 'viem'
import { CAPABILITY_GROUPS, type CapabilityGroupKey, DATA_KEYS, DATA_KEY_PRESETS, groupTagClasses, INTERFACE_PRESETS, LSP0_ABI, PERMISSION_CAPABILITY_GROUP, PERMISSION_NAMES, PERMISSION_TECHNICAL_NAMES, PERMISSIONS, permissionDetailPillClasses, permissionPillClasses } from '../constants'
import { fetchProfileFromIndexer, formatAddress } from '../utils'
import { ProfileDisplay } from './LuksoProfileAvatar'
import { useControllers } from '../hooks/useControllers'

// Minimal ABI to read contract name/symbol (ERC20, ERC721, LSP7, LSP8, etc.)
const NAME_SYMBOL_ABI = [
  { inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
] as const

// LSP4 Digital Asset Metadata data keys (ERC725Y) — fallback when name()/symbol() fail (e.g. LSP7/LSP8)
const LSP4_TOKEN_NAME_KEY = '0xdeba1e292f8ba88238e10ab3c7f88bd4be4fac56cad5194b6ecceaf653468af1' as Hex
const LSP4_TOKEN_SYMBOL_KEY = '0x2f0a68ab07768e01943a599e73362a0e17a63a72e94dd2e384d2c1d4db932756' as Hex
const ERC725Y_GET_DATA_ABI = [
  { inputs: [{ name: 'dataKey', type: 'bytes32' }], name: 'getData', outputs: [{ type: 'bytes', name: 'value' }], stateMutability: 'view', type: 'function' },
] as const

function decodeLSP2String(hex: Hex): string | undefined {
  if (!hex || hex === '0x' || hex.length < 4) return undefined
  const raw = hex.slice(2)
  try {
    // LSP2: can be length-prefixed (2 bytes) + UTF-8, or raw UTF-8
    if (raw.length >= 4) {
      const len = parseInt(raw.slice(0, 4), 16)
      if (len > 0 && raw.length >= 4 + len * 2) {
        return hexToString(('0x' + raw.slice(4, 4 + len * 2)) as Hex).replace(/\u0000+$/g, '').trim() || undefined
      }
    }
    return hexToString(hex as Hex).replace(/\u0000+$/g, '').trim() || undefined
  } catch {
    return undefined
  }
}

function hasPerm(perms: bigint, permHex: string) {
  return (perms & BigInt(permHex)) !== 0n
}

type AddressType = 'up' | 'eoa' | 'contract'

export function ControllersView({
  upAddress,
  publicClient,
  refreshToken,
  onStartAddController,
  onEditController,
  actionInProgressFor,
}: {
  upAddress: Address | null
  publicClient: PublicClient | null
  refreshToken?: number
  onStartAddController?: () => void
  onEditController?: (controllerAddress: Address) => void
  actionInProgressFor?: Address | null
}) {
  const { controllers, isLoading, error, reload } = useControllers({ upAddress, publicClient, refreshToken })
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [profilesByAddress, setProfilesByAddress] = useState<Record<string, { name: string | null; profileImageUrl: string | null }>>({})
  const [addressTypeByAddress, setAddressTypeByAddress] = useState<Record<string, AddressType>>({})
  const [contractMetadataByAddress, setContractMetadataByAddress] = useState<Record<string, { name?: string; symbol?: string }>>({})
  const [selectorNamesByHex, setSelectorNamesByHex] = useState<Record<string, string>>({})
  const [copiedItemKey, setCopiedItemKey] = useState<string | null>(null)

  const handleCopyItem = useCallback((text: string, key: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedItemKey(key)
      setTimeout(() => setCopiedItemKey(null), 2000)
    })
  }, [])

  const sorted = useMemo(() => {
    return [...controllers].sort((a, b) => a.address.localeCompare(b.address))
  }, [controllers])

  // Fetch controller name/image via indexer (best effort)
  useEffect(() => {
    let cancelled = false

    const knownAddresses = new Set<string>()
    for (const controller of sorted) {
      knownAddresses.add(controller.address.toLowerCase())
      for (const call of controller.decodedAllowedCalls ?? []) {
        if (call.address !== 'any') {
          knownAddresses.add(call.address.toLowerCase())
        }
      }
    }

    const missing = Array.from(knownAddresses)
      .filter((addr) => !profilesByAddress[addr.toLowerCase()])

    if (missing.length === 0) return

    Promise.allSettled(
      missing.map(async (addr) => {
        const p = await fetchProfileFromIndexer(addr as Address)
        return { addr: addr.toLowerCase(), p }
      })
    ).then((results) => {
      if (cancelled) return
      setProfilesByAddress((prev) => {
        const next = { ...prev }
        for (const r of results) {
          if (r.status === 'fulfilled') {
            next[r.value.addr] = {
              name: r.value.p?.name ?? null,
              profileImageUrl: r.value.p?.profileImageUrl ?? null,
            }
          }
        }
        return next
      })
    })

    return () => {
      cancelled = true
    }
  }, [sorted, profilesByAddress])

  useEffect(() => {
    let cancelled = false
    const missing = sorted
      .map((controller) => controller.address.toLowerCase())
      .filter((addr) => !addressTypeByAddress[addr])

    if (missing.length === 0 || !publicClient) return

    Promise.allSettled(
      missing.map(async (addr) => {
        const code = await publicClient.getCode({ address: addr as Address })
        if (!code || code === '0x') {
          return { addr, type: 'eoa' as AddressType }
        }

        try {
          await publicClient.readContract({
            address: addr as Address,
            abi: LSP0_ABI,
            functionName: 'owner',
          })
          await publicClient.readContract({
            address: addr as Address,
            abi: LSP0_ABI,
            functionName: 'getData',
            args: [DATA_KEYS['LSP3Profile'] as Hex],
          })
          return { addr, type: 'up' as AddressType }
        } catch {
          return { addr, type: 'contract' as AddressType }
        }
      })
    ).then((results) => {
      if (cancelled) return
      setAddressTypeByAddress((prev) => {
        const next = { ...prev }
        for (const result of results) {
          if (result.status === 'fulfilled') {
            next[result.value.addr] = result.value.type
          }
        }
        return next
      })
    })

    return () => {
      cancelled = true
    }
  }, [sorted, publicClient, addressTypeByAddress])

  // Fetch contract name/symbol for allowed-call targets (that have code and no profile name).
  // Process in small batches with delay to avoid RPC rate limits (429).
  const CONTRACT_METADATA_BATCH_SIZE = 4
  const CONTRACT_METADATA_BATCH_DELAY_MS = 200
  useEffect(() => {
    let cancelled = false
    const addresses = new Set<string>()
    for (const controller of sorted) {
      for (const call of controller.decodedAllowedCalls ?? []) {
        if (call.address !== 'any') {
          addresses.add(call.address.toLowerCase())
        }
      }
    }
    const toFetch = Array.from(addresses).filter(
      (addr) => !profilesByAddress[addr]?.name && !contractMetadataByAddress[addr]
    )
    if (toFetch.length === 0 || !publicClient) return

    const fetchOne = async (addr: string) => {
      const code = await publicClient!.getCode({ address: addr as Address })
      if (!code || code === '0x') return { addr, name: undefined, symbol: undefined }
      try {
        let name = await publicClient!.readContract({ address: addr as Address, abi: NAME_SYMBOL_ABI, functionName: 'name' }).catch(() => undefined) as string | undefined
        let symbol = await publicClient!.readContract({ address: addr as Address, abi: NAME_SYMBOL_ABI, functionName: 'symbol' }).catch(() => undefined) as string | undefined
        if (name === undefined || symbol === undefined) {
          const [nameBytes, symbolBytes] = await Promise.all([
            publicClient!.readContract({ address: addr as Address, abi: ERC725Y_GET_DATA_ABI, functionName: 'getData', args: [LSP4_TOKEN_NAME_KEY] }).catch(() => '0x' as Hex),
            publicClient!.readContract({ address: addr as Address, abi: ERC725Y_GET_DATA_ABI, functionName: 'getData', args: [LSP4_TOKEN_SYMBOL_KEY] }).catch(() => '0x' as Hex),
          ])
          if (name === undefined && nameBytes && nameBytes !== '0x') name = decodeLSP2String(nameBytes as Hex)
          if (symbol === undefined && symbolBytes && symbolBytes !== '0x') symbol = decodeLSP2String(symbolBytes as Hex)
        }
        return { addr, name, symbol }
      } catch {
        return { addr, name: undefined, symbol: undefined }
      }
    }

    const runBatched = async () => {
      for (let i = 0; i < toFetch.length; i += CONTRACT_METADATA_BATCH_SIZE) {
        if (cancelled) return
        const batch = toFetch.slice(i, i + CONTRACT_METADATA_BATCH_SIZE)
        const batchResults = await Promise.allSettled(batch.map(fetchOne))
        if (cancelled) return
        const updates: Array<{ addr: string; name?: string; symbol?: string }> = []
        for (const r of batchResults) {
          if (r.status === 'fulfilled' && (r.value.name !== undefined || r.value.symbol !== undefined)) {
            updates.push(r.value)
          }
        }
        if (updates.length > 0) {
          setContractMetadataByAddress((prev) => {
            const next = { ...prev }
            for (const { addr, name, symbol } of updates) {
              next[addr] = { name, symbol }
            }
            return next
          })
        }
        if (i + CONTRACT_METADATA_BATCH_SIZE < toFetch.length) {
          await new Promise((r) => setTimeout(r, CONTRACT_METADATA_BATCH_DELAY_MS))
        }
      }
    }
    runBatched()
    return () => {
      cancelled = true
    }
  }, [sorted, publicClient, profilesByAddress, contractMetadataByAddress])

  // Resolve function selectors to human-readable names (4byte.directory)
  useEffect(() => {
    let cancelled = false
    const selectors = new Set<string>()
    for (const controller of sorted) {
      for (const call of controller.decodedAllowedCalls ?? []) {
        if (call.selector !== 'any' && typeof call.selector === 'string') {
          selectors.add((call.selector as string).toLowerCase())
        }
      }
    }
    const toFetch = Array.from(selectors).filter((sel) => !selectorNamesByHex[sel])
    if (toFetch.length === 0) return

    Promise.allSettled(
      toFetch.map(async (hex) => {
        const res = await fetch(
          `https://www.4byte.directory/api/v1/signatures/?hex_signature=${encodeURIComponent(hex)}`
        )
        if (!res.ok) return { hex, name: null }
        const json = await res.json()
        const first = json.results?.[0]
        return { hex, name: first?.text_signature ?? null }
      })
    ).then((results) => {
      if (cancelled) return
      setSelectorNamesByHex((prev) => {
        const next = { ...prev }
        for (const r of results) {
          if (r.status === 'fulfilled' && r.value.name) {
            next[r.value.hex] = r.value.name
          }
        }
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [sorted, selectorNamesByHex])

  if (!upAddress || !publicClient) {
    return (
      <div className="card-hub">
        <h3 className="text-lg font-semibold">Controllers</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Connect a Universal Profile to view its authorized controllers.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Controllers {controllers.length}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            These addresses have permissions on your Universal Profile
          </p>
        </div>
        {onStartAddController && (
          <button
            type="button"
            className="btn btn-primary text-sm shrink-0 w-full sm:w-auto"
            onClick={onStartAddController}
          >
            Add controller
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {isLoading && sorted.length === 0 && (
        <div className="card-hub">
          <div className="text-sm text-gray-600 dark:text-gray-400">Loading controllers…</div>
        </div>
      )}

      {!isLoading && sorted.length === 0 && (
        <div className="card-hub">
          <h3 className="text-lg font-semibold">No controllers found</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            This Universal Profile doesn’t have any controller entries in <span className="font-mono">AddressPermissions[]</span>.
          </p>
        </div>
      )}

      {sorted.map((c) => {
        const isOpen = !!expanded[c.address]
        const profile = profilesByAddress[c.address.toLowerCase()]
        const addressType = addressTypeByAddress[c.address.toLowerCase()]
        const isBusy = actionInProgressFor?.toLowerCase() === c.address.toLowerCase()
        const displayName =
          c.controllerLabel ||
          profile?.name ||
          (addressType === 'contract' ? 'Smart contract' : addressType === 'up' ? 'Universal Profile' : 'EOA')

        const hasSuperCall = hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_CALL)
        const hasCall =
          hasPerm(c.permissionsBigInt, PERMISSIONS.CALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.STATICCALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.DELEGATECALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.TRANSFERVALUE)

        const hasSuperSetData = hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_SETDATA)
        const hasSetData = hasPerm(c.permissionsBigInt, PERMISSIONS.SETDATA)
        const hasSuperTransfer = hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_TRANSFERVALUE)
        const hasTransfer = hasPerm(c.permissionsBigInt, PERMISSIONS.TRANSFERVALUE)
        const hasContractCalls =
          hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_CALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.CALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.DELEGATECALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_DELEGATECALL)
        const hasDeployOrEditContracts =
          hasPerm(c.permissionsBigInt, PERMISSIONS.DEPLOY) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.DELEGATECALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_DELEGATECALL)
        const hasCoreSettings =
          hasPerm(c.permissionsBigInt, PERMISSIONS.ADDCONTROLLER) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.EDITPERMISSIONS) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.CHANGEOWNER) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.ADDEXTENSIONS) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.CHANGEEXTENSIONS) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.ADDUNIVERSALRECEIVERDELEGATE) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.CHANGEUNIVERSALRECEIVERDELEGATE) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.REENTRANCY)

        const groupedPermissionKeys = CAPABILITY_GROUPS.reduce<Record<CapabilityGroupKey, string[]>>((acc, g) => {
          acc[g.key] = []
          return acc
        }, {
          identitySigning: [],
          profileData: [],
          contractInteractions: [],
          valueTransfer: [],
          administrationOwnership: [],
        })

        for (const key of c.permissionKeys) {
          const group = PERMISSION_CAPABILITY_GROUP[key]
          if (!group) continue
          groupedPermissionKeys[group].push(key)
        }

        const contractScope = (
          hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_CALL) ||
          hasPerm(c.permissionsBigInt, PERMISSIONS.SUPER_DELEGATECALL) ||
          ((hasPerm(c.permissionsBigInt, PERMISSIONS.CALL) || hasPerm(c.permissionsBigInt, PERMISSIONS.DELEGATECALL)) && !c.allowedCallsHex)
        )
          ? 'full access'
          : (hasPerm(c.permissionsBigInt, PERMISSIONS.CALL) || hasPerm(c.permissionsBigInt, PERMISSIONS.DELEGATECALL))
            ? 'restricted'
            : null

        const dataScope = hasSetData
          ? (hasSuperSetData || !c.allowedDataKeysHex ? 'full access' : 'restricted')
          : null

        const summaryTags: Array<{ group: CapabilityGroupKey; label: string }> = []
        if (groupedPermissionKeys.identitySigning.length > 0) {
          summaryTags.push({ group: 'identitySigning', label: 'Read & sign' })
        }
        if (hasSetData || hasSuperSetData) {
          summaryTags.push({
            group: 'profileData',
            label: dataScope === 'restricted' ? 'Edit some metadata' : 'Edit all metadata',
          })
        }
        if (hasContractCalls) {
          summaryTags.push({
            group: 'contractInteractions',
            label: contractScope === 'restricted' ? 'Call some contracts' : 'Call all contracts',
          })
        }
        if (hasDeployOrEditContracts) {
          summaryTags.push({ group: 'contractInteractions', label: 'Deploy and edit contracts' })
        }
        if (hasTransfer || hasSuperTransfer) {
          summaryTags.push({
            group: 'valueTransfer',
            label: hasSuperTransfer ? 'Spend unlimited LYX' : 'Spend LYX',
          })
        }
        if (hasCoreSettings) {
          summaryTags.push({ group: 'administrationOwnership', label: 'Change settings' })
        }

        return (
          <div key={c.address} className="card-hub">
            <div
              role="button"
              tabIndex={0}
              className="w-full text-left cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-cta-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-lukso-dark"
              onClick={() => setExpanded((prev) => ({ ...prev, [c.address]: !prev[c.address] }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setExpanded((prev) => ({ ...prev, [c.address]: !prev[c.address] }))
                }
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <ProfileDisplay
                    address={c.address}
                    profileUrl={profile?.profileImageUrl}
                    name={displayName}
                    size="md"
                    showCopyAddress
                  />
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 pb-1">
                    <span className={permissionPillClasses('bg-white/60 dark:bg-gray-950/30 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800')}>
                      {c.permissionKeys.length} permission{c.permissionKeys.length === 1 ? '' : 's'}
                    </span>
                    {summaryTags.length === 0 && (
                      <span className={permissionPillClasses(groupTagClasses('identitySigning'))}>No access</span>
                    )}
                    {summaryTags.map((tag, i) => (
                      <span key={`${c.address}-summary-${i}-${tag.label}`} className={permissionPillClasses(groupTagClasses(tag.group))}>
                        {tag.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex-shrink-0 pt-1 text-gray-400">
                  <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {isOpen && (
              <div className="mt-5 border-t border-gray-200 dark:border-gray-800 pt-5 space-y-5">
                <div>
                  <div className="text-sm font-semibold">All Permissions</div>
                  <div className="mt-3 space-y-3">
                    {c.permissionKeys.length === 0 && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">No permissions set.</span>
                    )}
                    {CAPABILITY_GROUPS.map((group) => {
                      const keys = groupedPermissionKeys[group.key]
                      const scope =
                        group.key === 'contractInteractions'
                          ? contractScope
                          : group.key === 'profileData'
                            ? dataScope
                            : null
                      return (
                        <div key={`${c.address}-group-row-${group.key}`}>
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                            {group.label}
                            {scope ? (
                              <span className="ml-2 text-[11px] text-gray-500 dark:text-gray-400">
                                ({scope})
                              </span>
                            ) : null}
                          </div>
                          {keys.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {keys.map((k) => {
                                const label = PERMISSION_NAMES[k] || k
                                const technical = PERMISSION_TECHNICAL_NAMES[k] || k
                                return (
                                  <div key={`${c.address}-perm-${k}`} className={permissionDetailPillClasses(groupTagClasses(group.key))}>
                                    <span className="text-[12px] leading-tight">{label}</span>
                                    <span className="text-[10px] leading-tight opacity-75 font-mono">{technical}</span>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 dark:text-gray-400">None</div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                </div>

                <div>
                  <div className="text-sm font-semibold">Allowed Contract Calls</div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {hasSuperCall
                      ? 'SUPER_CALL is enabled, this controller can call any contract without restrictions.'
                      : c.allowedCallsHex
                        ? 'Restrictions are set onchain. Expand entries below.'
                        : hasCall
                          ? 'No AllowedCalls restrictions set (but call-related permissions exist).'
                          : 'Not applicable (no call-related permissions).'}
                  </div>

                  {!hasSuperCall && c.decodedAllowedCalls && c.decodedAllowedCalls.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {c.decodedAllowedCalls.map((e, idx) => {
                        const target = formatAllowedCallTarget(e.address, profilesByAddress, contractMetadataByAddress)
                        const selectorKey = e.selector !== 'any' && typeof e.selector === 'string' ? (e.selector as string).toLowerCase() : ''
                        const rawFunctionLabel = selectorKey && selectorNamesByHex[selectorKey]
                          ? selectorNamesByHex[selectorKey]
                          : e.selector === 'any'
                            ? 'any'
                            : String(e.selector)
                        const functionLabel = typeof rawFunctionLabel === 'string' && rawFunctionLabel.includes('(')
                          ? rawFunctionLabel.slice(0, rawFunctionLabel.indexOf('('))
                          : rawFunctionLabel
                        const interfacePreset = e.interfaceId !== 'any' ? Object.values(INTERFACE_PRESETS).find((p) => p.id.toLowerCase() === (e.interfaceId as string).toLowerCase()) : null
                        const interfaceLabel = interfacePreset ? `${interfacePreset.name} (${e.interfaceId})` : (e.interfaceId === 'any' ? 'any' : e.interfaceId)
                        const acKey = `${c.address}-ac-${idx}`
                        const isCopied = copiedItemKey === acKey
                        return (
                          <div key={acKey} className="rounded-lg border border-white/5 bg-surface px-3 py-2">
                            <div className="text-sm text-gray-200 flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium">{e.callTypes.join(' + ')}</span>
                              <span className="text-gray-400"> to </span>
                              <span className="font-medium">{target.primary}</span>
                              {target.secondary && (
                                <span className="text-gray-500 font-mono text-xs">({target.secondary})</span>
                              )}
                              {e.address !== 'any' && (
                                <button
                                  type="button"
                                  onClick={(ev) => {
                                    ev.preventDefault()
                                    ev.stopPropagation()
                                    handleCopyItem(e.address as string, acKey)
                                  }}
                                  className="p-1 rounded text-gray-400 hover:text-gray-300 hover:bg-white/5 transition-colors shrink-0"
                                  title="Copy address"
                                  aria-label="Copy address"
                                >
                                  {isCopied ? (
                                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M9 7h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2V9a2 2 0 012-2z" />
                                    </svg>
                                  )}
                                </button>
                              )}
                              {interfacePreset && (
                                <span className="ml-0.5 text-xs text-gray-500 dark:text-gray-400">· {interfacePreset.name}</span>
                              )}
                            </div>
                            <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                              <span>Interface: </span>
                              <span className="font-mono">{interfaceLabel}</span>
                              <span className="mx-1.5">·</span>
                              <span>Function: </span>
                              <span className="font-mono">{functionLabel}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-sm font-semibold">Allowed Data Changes</div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {hasSuperSetData
                      ? 'SUPER_SETDATA is enabled, this controller can write any data key without restrictions.'
                      : c.allowedDataKeysHex
                        ? 'Restrictions are set onchain. Expand entries below.'
                        : hasSetData
                          ? 'No AllowedERC725YDataKeys restrictions set (but SETDATA exists).'
                          : 'Not applicable (no SETDATA permission).'}
                  </div>

                  {!hasSuperSetData && c.decodedAllowedDataKeys && c.decodedAllowedDataKeys.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {c.decodedAllowedDataKeys.map((k, idx) => {
                        const preset = Object.values(DATA_KEY_PRESETS).find((p) => p.key.toLowerCase() === k.toLowerCase())
                        const adkKey = `${c.address}-adk-${idx}`
                        const isCopied = copiedItemKey === adkKey
                        return (
                          <div key={adkKey} className="rounded-lg border border-white/5 bg-surface px-3 py-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs text-gray-700 dark:text-gray-200 font-mono break-words overflow-wrap-anywhere">{k}</span>
                              <button
                                type="button"
                                onClick={(ev) => {
                                  ev.preventDefault()
                                  ev.stopPropagation()
                                  handleCopyItem(k, adkKey)
                                }}
                                className="p-1 rounded text-gray-400 hover:text-gray-300 hover:bg-white/5 transition-colors shrink-0"
                                title="Copy data key"
                                aria-label="Copy data key"
                              >
                                {isCopied ? (
                                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M9 7h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2V9a2 2 0 012-2z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                            {preset && (
                              <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                {preset.name} <span className="opacity-60">•</span> {preset.group}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div className="pt-2 flex flex-wrap items-center gap-2">
                  {onEditController && (
                    <button
                      type="button"
                      className="btn-secondary text-sm"
                      disabled={isBusy}
                      onClick={() => onEditController(c.address)}
                    >
                      Edit permissions
                    </button>
                  )}
                  <button
                    type="button"
                    className="p-2 text-gray-400 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none"
                    onClick={reload}
                    disabled={isLoading || isBusy}
                    title="Refresh details"
                    aria-label="Refresh details"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}

    </div>
  )
}

function readableTarget(
  address: Address,
  profilesByAddress: Record<string, { name: string | null; profileImageUrl: string | null }>,
  contractMetadataByAddress?: Record<string, { name?: string; symbol?: string }>
) {
  const key = address.toLowerCase()
  const profile = profilesByAddress[key]
  if (profile?.name) {
    return { primary: profile.name, secondary: formatAddress(address, 4) }
  }
  const meta = contractMetadataByAddress?.[key]
  if (meta?.name) {
    const symbolPart = meta.symbol ? ` (${meta.symbol})` : ''
    return { primary: `${meta.name}${symbolPart}`, secondary: formatAddress(address, 4) }
  }
  return { primary: formatAddress(address, 6), secondary: null }
}

function formatAllowedCallTarget(
  address: Address | 'any',
  profilesByAddress: Record<string, { name: string | null; profileImageUrl: string | null }>,
  contractMetadataByAddress: Record<string, { name?: string; symbol?: string }>
): { primary: string; secondary: string | null } {
  if (address === 'any') {
    return { primary: 'any contract', secondary: null }
  }
  return readableTarget(address, profilesByAddress, contractMetadataByAddress)
}
